import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser } from '@/auth/AuthProvider'
import { queryKeys } from '@/lib/queryClient'
import {
  ensureUser,
  fetchUser,
  provisionIngestAddress,
  updateUserProfile,
  type UserProfileUpdate,
} from './api'

/**
 * The signed-in user's profile document, created on first read if absent.
 *
 * Creating it lazily here rather than in an auth callback keeps profile
 * creation on a path that can retry: a failed write surfaces as a query error
 * the user can act on, instead of leaving a signed-in session with no profile
 * and no way to notice.
 */
export function useUserProfile() {
  const user = useCurrentUser()
  return useQuery({
    queryKey: queryKeys.user(user.uid),
    queryFn: () =>
      ensureUser(user.uid, user.email ?? '', user.displayName ?? user.email ?? 'User'),
  })
}

export function useUpdateUserProfile() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (patch: UserProfileUpdate) => updateUserProfile(user.uid, patch),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.user(user.uid) }),
  })
}

/**
 * Issue (or re-read) the personal forwarding address.
 *
 * Idempotent on the server: calling it twice returns the same address rather
 * than reissuing one, so a double-click can't invalidate a forwarding rule
 * already set up in a mail client.
 */
export function useProvisionIngestAddress() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => provisionIngestAddress(),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.user(user.uid) }),
  })
}

export { fetchUser }
